'use client';
import { IProductCart, ISectionItem } from '@/shared/api/getProduct/types';
import { postHistory } from '@/shared/api/postHistory';
import { Breadcrumbs } from '@/shared/ui/Breadcrumbs';
import { Container } from '@/shared/ui/Container';
import { Tag } from '@/shared/ui/Tag';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { DetailsForm } from '@/widgets/product-details/ui/DetailsForm';
import { Gallery } from '@/widgets/product-details/ui/Gallery';
import { cva } from 'class-variance-authority';
import { FC, useEffect } from 'react';
import StickyBox from 'react-sticky-box';
import { useMedia } from 'react-use';
import { productTagsDataByTitleMock } from '../../../../data/productTagsDataMock';

interface Props {
  className?: string;
  data: IProductCart;
}

const buildBreadcrumbs = (section: ISectionItem | null) => {
  const breadcrumbs: Array<{
    title: string;
    href: string;
  }> = [];
  let currentPath = '/products';

  const sections = [];

  while (section) {
    sections.unshift(section);
    section = section.parent;
  }

  sections.forEach((sec) => {
    currentPath = `${currentPath}/${sec.code}`;

    breadcrumbs.push({
      title: sec.name,
      href: currentPath,
    });
  });

  return breadcrumbs;
};

export const ProductDetails: FC<Props> = ({ className, data }) => {
  const isMobile = useMedia('(max-width: 979px)', true);

  useEffect(() => {
    postHistory(data.id);
    window.scrollTo(0, 0);
  }, []);
  return (
    <Container className={twMergeExt(cvaRoot(), className)} variant={'full'}>
      {isMobile ? (
        <div className={cvaLeft()}>
          <Breadcrumbs
            className={cvaBreadcrumbs()}
            data={[
              {
                title: 'Главная',
                href: '/',
              },
              ...buildBreadcrumbs(data.sections),
              {
                title: data.name,
              },
            ]}
            swiperBreakpoints={{
              1024: {
                slidesOffsetBefore: 42,
                slidesOffsetAfter: 42,
              },
              1440: {
                slidesOffsetBefore: 56,
                slidesOffsetAfter: 56,
              },
            }}
          />
          <Gallery images={data.photos} />
        </div>
      ) : (
        <StickyBox className={cvaLeft()}>
          <Breadcrumbs
            className={cvaBreadcrumbs()}
            data={[
              {
                title: 'Главная',
                href: '/',
              },
              ...buildBreadcrumbs(data.sections),
              {
                title: data.name,
              },
            ]}
            swiperBreakpoints={{
              1024: {
                slidesOffsetBefore: 42,
                slidesOffsetAfter: 42,
              },
              1440: {
                slidesOffsetBefore: 56,
                slidesOffsetAfter: 56,
              },
            }}
          />
          <Gallery images={data.photos} />
        </StickyBox>
      )}
      <div className={cvaTags()}>
        {data.label?.map((i, index) => <Tag key={index} {...productTagsDataByTitleMock[i.id]} />)}
      </div>
      <DetailsForm
        vendorCode={data.article}
        brand={data.brand}
        id={data.id}
        rating={data.rating}
        reviewsCount={data.reviews?.length}
        title={data.name}
        volume={data.volume ? `${data.volume} мл` : ''}
        weight={data.weight ? `${data.weight} гр` : ''}
        descr={data.original_name || ''}
        price={data.price}
        discountPrice={data.discount_price}
        isFavorite={data.is_favorite}
        data={data}
      />
    </Container>
  );
};

const cvaRoot = cva(['ProductDetails-cvaRoot', 'relative', 'md:flex']);

const cvaLeft = cva([
  'ProductDetails-cvaLeft',
  'relative',
  'flex-1 self-start overflow-hidden',
  'rounded-[6.4rem]',
  'bg-cLightGrey',
]);

const cvaBreadcrumbs = cva(['ProductDetails-cvaBreadcrumbs', ' mt-2.4 ml-3 md:ml-0 flex-nowrap md:flex-wrap']);

const cvaTags = cva([
  'ProductDetails-cvaTags',
  'absolute top-[12.4rem] md:left-[35.7rem] lg:left-[54.6rem]',
  'flex flex-wrap justify-end gap-0.4',
  'w-[16.8rem] z-20',
]);
